import React, { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  message,
  Popconfirm,
  Tag,
  Space,
  Typography,
  Alert,
  Tooltip,
  Row,
  Col,
  Statistic,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  CopyOutlined,
  KeyOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import api from '../../services/api';

const { Title, Text, Paragraph } = Typography;

interface ERPAPIKey {
  id: string;
  key_hash: string;
  key_name: string;
  created_at: string;
  expires_at: string | null;
  last_used: string | null;
  usage_count: number;
  is_active: boolean;
}

interface NewKeyResponse {
  api_key: string;
  key_id: string;
  expires_at: string | null;
}

const ERPAPIKeysPage: React.FC = () => {
  const [keys, setKeys] = useState<ERPAPIKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [newKeyModalVisible, setNewKeyModalVisible] = useState(false);
  const [newKeyData, setNewKeyData] = useState<NewKeyResponse | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchAPIKeys();
  }, []);

  const fetchAPIKeys = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/super-admin/api-keys');
      setKeys(response.data.api_keys || []);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    form.resetFields();
    form.setFieldsValue({
      expires_in_days: 365,
    });
    setModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const response = await api.post('/api/super-admin/api-keys', values);
      
      setNewKeyData(response.data);
      setModalVisible(false);
      setNewKeyModalVisible(true);
      
      fetchAPIKeys();
    } catch (error: any) {
      if (error.errorFields) {
        return;
      }
      message.error(error.response?.data?.message || 'Failed to create API key');
    }
  };

  const handleRevoke = async (keyId: string) => {
    try {
      await api.post(`/api/super-admin/api-keys/${keyId}/revoke`);
      message.success('API key revoked successfully');
      fetchAPIKeys();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to revoke API key');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success('Copied to clipboard!');
  };

  const columns = [
    {
      title: 'Description',
      dataIndex: 'key_name',
      key: 'key_name',
      render: (desc: string) => <Text strong>{desc}</Text>,
    },
    {
      title: 'Key Hash',
      dataIndex: 'key_hash',
      key: 'key_hash',
      render: (hash: string) => (
        <Text code>{hash ? hash.substring(0, 12) + '...' : 'N/A'}</Text>
      ),
    },
    {
      title: 'Usage',
      dataIndex: 'usage_count',
      key: 'usage_count',
      render: (count: number) => (
        <Statistic value={count} valueStyle={{ fontSize: '14px' }} />
      ),
    },
    {
      title: 'Last Used',
      dataIndex: 'last_used',
      key: 'last_used',
      render: (date: string | null) => (
        date ? new Date(date).toLocaleString() : <Text type="secondary">Never</Text>
      ),
    },
    {
      title: 'Expires',
      dataIndex: 'expires_at',
      key: 'expires_at',
      render: (date: string | null) => {
        if (!date) return <Tag color="green">Never</Tag>;
        
        const expiresAt = new Date(date);
        const now = new Date();
        const daysUntilExpiry = Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        
        if (daysUntilExpiry < 0) {
          return <Tag color="red">Expired</Tag>;
        } else if (daysUntilExpiry < 30) {
          return <Tag color="orange">{daysUntilExpiry} days left</Tag>;
        } else {
          return <Tag color="green">{expiresAt.toLocaleDateString()}</Tag>;
        }
      },
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Revoked'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: ERPAPIKey) => (
        record.is_active ? (
          <Popconfirm
            title="Revoke this API key?"
            description="This action cannot be undone. All requests using this key will fail."
            onConfirm={() => handleRevoke(record.id)}
            okText="Revoke"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              Revoke
            </Button>
          </Popconfirm>
        ) : (
          <Text type="secondary">Revoked</Text>
        )
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
        <Title level={2}>ERP API Keys Management</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
        >
          Generate New Key
        </Button>
      </div>

      <Alert
        message="API Key Security"
        description="API keys are used for ERP integration (Kiyzer.ai/Odoo). Keep them secure. Keys are only shown once at creation time."
        type="warning"
        showIcon
        icon={<WarningOutlined />}
        style={{ marginBottom: '16px' }}
      />

      <Card>
        <Table
          columns={columns}
          dataSource={keys}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {/* Create API Key Modal */}
      <Modal
        title={<><KeyOutlined /> Generate New API Key</>}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="key_name"
            label="Description"
            rules={[{ required: true, message: 'Please enter a description' }]}
          >
            <Input placeholder="e.g., Kiyzer.ai Production Key" />
          </Form.Item>

          <Form.Item
            name="expires_in_days"
            label="Expires In (Days)"
            tooltip="Leave empty for no expiration"
          >
            <InputNumber
              min={1}
              style={{ width: '100%' }}
              placeholder="e.g., 365"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Display New Key Modal */}
      <Modal
        title={<Text type="danger"><WarningOutlined /> API Key Created - Save This Now!</Text>}
        open={newKeyModalVisible}
        onOk={() => setNewKeyModalVisible(false)}
        onCancel={() => setNewKeyModalVisible(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setNewKeyModalVisible(false)}>
            I have saved the key
          </Button>
        ]}
        closable={false}
        maskClosable={false}
        width={700}
      >
        <Alert
          message="IMPORTANT: This key will only be shown once!"
          description="Copy and save this API key securely. You will not be able to see it again."
          type="error"
          showIcon
          style={{ marginBottom: '16px' }}
        />

        {newKeyData && (
          <>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Card size="small" style={{ backgroundColor: '#f5f5f5' }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Text strong>API Key:</Text>
                    <Input.TextArea
                      value={newKeyData.api_key}
                      readOnly
                      rows={3}
                      style={{ fontFamily: 'monospace', fontSize: '12px' }}
                    />
                    <Button
                      type="primary"
                      icon={<CopyOutlined />}
                      onClick={() => copyToClipboard(newKeyData.api_key)}
                      block
                    >
                      Copy to Clipboard
                    </Button>
                  </Space>
                </Card>
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
              <Col span={12}>
                <Text type="secondary">Key ID:</Text>
                <br />
                <Text code>{newKeyData.key_id}</Text>
              </Col>
              <Col span={12}>
                <Text type="secondary">Expires:</Text>
                <br />
                <Text>{newKeyData.expires_at ? new Date(newKeyData.expires_at).toLocaleDateString() : 'Never'}</Text>
              </Col>
            </Row>

            <Alert
              message="Usage Example"
              description={
                <Paragraph code style={{ marginTop: '8px' }}>
                  curl -H "X-API-Key: {newKeyData.api_key}" https://api.texo.com/api/erp/tenants/...
                </Paragraph>
              }
              type="info"
              style={{ marginTop: '16px' }}
            />
          </>
        )}
      </Modal>
    </div>
  );
};

export default ERPAPIKeysPage;
